/* eslint-disable */

/* global gform, gforms_square_theme_strings */
import helpers from './helpers';
import jQuery from 'jquery';
/**
 * Handles rendering the web payment card and updating its UI.
 */
export default class CardRenderer {
	pageType = null;

	/**
	 * @function constructor
	 * @description Card Renderer Constructor.
	 *
	 * @param {WebPayments}      webPayments  Square Web Payments SDK Wrapper.
	 * @param {a11yErrorHandler} errorStack   Errors a11y Handler.
	 * @param {number}           formId       The current Form ID.
	 * @param                    pageInstance
	 * @param                    strings
	 * @param {number}           fieldId      The Square Field ID.
	 * @param {object}           styleObject  The style object that contains the style selectors, properties and values that will be passed to the square SDK.
	 * @param {boolean}          pageType     Are we on the front end or the block editor.
	 */
	constructor( webPayments, errorStack, formId, pageInstance, fieldId, styleObject, pageType = null, strings ) {
		this.pageType = pageType;
		this.webPayments = webPayments;
		this.errorStack = errorStack;
		this.formId = formId;
		this.pageInstance = pageInstance;
		this.fieldId = fieldId;
		this.field = null;
		this.cardHolderNameInput = null;
		this.cardContainer = null;
		this.isCardVisible = false;
		this.styleObject = styleObject;
		this.pageType = pageType;
		this.strings = strings;
		this.cardStyle = {};
		this.card = null;
		this.bindEvents();
	}

	/**
	 * @function bindEvents
	 * @description Binds events handlers for the form and feed conditional logic.
	 *
	 * @since 1.7
	 *
	 */
	async bindEvents() {
		// Render the web payment card.
		if ( this.pageType !== 'front' ) {
			jQuery( document ).ready( await this.render.bind( this ) );
		}

		// After conditional logic evaluation, trigger window resize to let the square IFrame re-calculate its size if the field became visible.
		jQuery( document ).on(
			'gform_post_conditional_logic',
			this.handlePostConditionalLogic.bind( this )
		);

		gform.addAction( 'gform_frontend_feeds_evaluated', this.handlePostConditionalLogic.bind( this ) );

		this.cardStyle = this.getCardConfigurationStyleObject();
	}

	/**
	 * @function cacheElements
	 * @description Caches the elements used in the process of creating and submitting the form.
	 *
	 * @since 1.7
	 *
	 */
	cacheElements() {
		this.field = document.getElementById( 'field_' + this.formId + '_' + this.fieldId );
		this.cardHolderNameInput = document.getElementById( 'input_' + this.formId + '_' + this.fieldId + '_3' );
		this.cardContainer = document.getElementById( 'input_' + this.formId + '_' + this.fieldId + '_1' );
		this.fieldContainer = document.getElementById( 'input_' + this.formId + '_' + this.fieldId );
	}

	/**
	 * @function render
	 * @description Renders the square web payment card.
	 *
	 * @since 1.7
	 *
	 * @return {Promise<void>}
	 */
	async render() {
		this.cacheElements();

		try {
			const styles = this.getCardConfigurationStyleObject();
			this.card = await this.webPayments.getCard( styles );

			this.attached = await this.card.attach( this.cardContainer );

			/* When the field is rendered while hidden, the height of the IFrame is not calculated correctly
			this is an issue on the Square side, after conditional logic we re-configure the card and let it resize
			properly, so we have to hide it until we are sure it looks OK, otherwise it will look broken for a fraction
			of a second. */
			if ( this.fieldContainer !== null && this.cardContainer !== null ) {
				// Apply the theme framework control component class to the square card field wrapper
				this.cardContainer.querySelector( '.sq-card-iframe-container' ).classList.add( 'gform-theme-field-control' );

				if ( jQuery( this.fieldContainer ).is( ':hidden' ) ) {
					this.cardContainer.style.visibility = 'hidden';
				}
			}

			this.bindCardEventListeners.bind( this );
			this.bindCardEventListeners( this.card );
		} catch ( e ) {
			console.log( e );
			if ( this.pageType === 'front' ) {
				this.errorStack.push( 'generic', this.strings.CATCH_ALL );
			}
		}
	}

	/**
	 * @function getCardConfigurationStyleObject
	 * @description Calculates the style for the cardholder name input, to be used for the inputs inside the square IFrame.
	 *
	 * @since 1.7
	 *
	 */
	getCardConfigurationStyleObject() {
		// If this has keys, the theme framework is being used and we can bail here.
		if ( Object.keys( this.styleObject ).length !== 0 ) {
			return this.styleObject;
		}

		let styleObject = {};
		let formControl = this.cardHolderNameInput === null ? document.querySelector( 'input.sq-input' ) : this.cardHolderNameInput;

		// Only run this if the Square element is present
		if ( ! formControl ) {
			return {};
		}

		// Setup default styles based on the card name input
		const sqInputStyle = window.getComputedStyle( formControl );
		styleObject = {
			'.input-container': {
				borderColor: sqInputStyle.getPropertyValue( 'border-color' ),
				borderRadius: sqInputStyle.getPropertyValue( 'border-radius' ),
				borderWidth: sqInputStyle.getPropertyValue( 'border-width' ),
			},
			input: {
				backgroundColor: sqInputStyle.getPropertyValue( 'background-color' ),
				color: sqInputStyle.getPropertyValue( 'color' ),
				fontSize: sqInputStyle.getPropertyValue( 'font-size' ),
				fontWeight: sqInputStyle.getPropertyValue( 'font-weight' ),
			},
			'input::placeholder': {
				color: sqInputStyle.getPropertyValue( 'color' ),
			},
		};

		/**
		 * Filter the style object that is passed to the square SDK to style the credit card details iframe and its contents.
		 *
		 * @see https://developer.squareup.com/reference/sdks/web/payments/objects/CardClassSelectors
		 *
		 * @since 1.7
		 *
		 * @param {object} styleObject The style object that contains the style selectors, properties and values.
		 * @param {Int}    formId      The formId.
		 *
		 * @return {object} The filtered style object.
		 */
		styleObject = gform.applyFilters( 'gform_square_card_details_style', styleObject, this.formId );

		return styleObject;
	}

	/**
	 * @function handlePostConditionalLogic
	 * @description Runs after form conditional logic has been evaluated, triggers a resize event and reconfigures the card.
	 *
	 * @since 1.7
	 *
	 */
	handlePostConditionalLogic() {
		// The event is fired many times for a single change.
		// Clear the timeout of the previous calls so the logic eventually runs once.
		if ( this.conditionalLogicTimeout !== null ) {
			clearTimeout( this.conditionalLogicTimeout );
		}

		this.conditionalLogicTimeout = setTimeout( () => {
			if ( this.field !== null && jQuery( this.field ).is( ':visible' ) ) {
				// Get the style object again after the input has become visible.
				this.webPayments.reConfigureCard( {
					style: this.getCardConfigurationStyleObject(),
				} );

				// Trigger window resize to let the square IFrame re-calculate its size if the field became visible.
				const ResizeEvent = document.createEvent( 'HTMLEvents' );
				ResizeEvent.initEvent( 'resize', true, false );
				window.dispatchEvent( ResizeEvent );

				if ( this.cardContainer !== null ) {
					this.cardContainer.style.visibility = 'visible';
				}
			}
		 }, 250 );
	}

	/**
	 * @function bindCardEventListeners
	 * @description Binds event listeners for UI events of the card.
	 *
	 * @since 1.7
	 *
	 * @param {object} card The Square card.
	 */
	bindCardEventListeners( card ) {
		card.addEventListener( 'focusClassAdded', this.handleCardUIChange.bind( this ) );
		card.addEventListener( 'focusClassRemoved', this.handleCardUIChange.bind( this ) );
		card.addEventListener( 'errorClassAdded', this.handleCardUIChange.bind( this ) );
		card.addEventListener( 'errorClassRemoved', this.handleCardUIChange.bind( this ) );
		card.addEventListener( 'escape', this.handleCardUIChange.bind( this ) );
	}

	/**
	 * @function handleCardUIChange
	 * @description Handles UI for when the card loses/gets focus.
	 *
	 * When the card gets focus, a textual message appears beneath it, this function adds a margin for the label when it is below the card
	 * so the textual message don't overflow over the label, it also resets the margin once the card loses focus as the textual message disappears.
	 *
	 * @since 1.7
	 *
	 * @param {object} event The card UI event.
	 *
	 * @return {Promise<void>}
	 */
	async handleCardUIChange( event ) {
		if ( this.pageType === 'block' ) {
			return;
		}
		switch ( event.type ) {
			case 'focusClassAdded':
			case 'errorClassAdded':
				// The SDK provides localized messages
				// The container is hidden because it doesn't match our UI
				// So we are going to take the text and push it to our error handler.
				if ( event.type === 'errorClassAdded' ) {
					const sdkMessage = jQuery( '#gform_' + this.formId + ' .sq-card-message' ).text();
					this.errorStack.push( event.detail.field, sdkMessage, true );
				}

				break;
			case 'focusClassRemoved':
			case 'errorClassRemoved':

				if ( event.type === 'errorClassRemoved' ) {
					this.errorStack.remove( event.detail.field );

					// If this was the last error to be cleared, reset our error stack as well.
					if ( event.detail.currentState.hasErrorClass === false ) {
						this.errorStack.clearErrors();
					}
				}

				break;
		}
	}
}
